/**
 * Distraction Report API — Meeting Tools Analytics.
 */

import { livekitAuthService as authService } from "../utils/authService";

export type DistractionReportStudent = {
  student_id: string;
  name: string;
  present: boolean;
  /** True when Redis last_seen is within the active threshold (180s). */
  active: boolean;
  /** Minutes the student was present in the session. */
  attended_minutes: number;
  tab_switches: number;
  tab_switch_seconds: number;
  tab_switch_minutes: number;
  minimize_count: number;
  minimize_seconds: number;
  minimize_minutes: number;
  alert_popup_events: number;
  alert_popup_seconds: number;
  alert_popup_minutes: number;
  missed_quizzes: number;
  poll_non_responses: number;
};

export type DistractionReportSummary = {
  students_tracked: number;
  tab_switches: number;
  tab_switch_seconds: number;
  tab_switch_minutes: number;
  minimize_count: number;
  minimize_seconds: number;
  minimize_minutes: number;
  alert_popup_events: number;
  alert_popup_seconds: number;
  alert_popup_minutes: number;
  missed_quizzes: number;
  poll_non_responses: number;
  completed_quizzes: number;
  ended_polls: number;
};

export type DistractionReport = {
  success: boolean;
  session_id: string;
  summary: DistractionReportSummary;
  students: DistractionReportStudent[];
};

const emptySummary: DistractionReportSummary = {
  students_tracked: 0,
  tab_switches: 0,
  tab_switch_seconds: 0,
  tab_switch_minutes: 0,
  minimize_count: 0,
  minimize_seconds: 0,
  minimize_minutes: 0,
  alert_popup_events: 0,
  alert_popup_seconds: 0,
  alert_popup_minutes: 0,
  missed_quizzes: 0,
  poll_non_responses: 0,
  completed_quizzes: 0,
  ended_polls: 0,
};

function asStudent(raw: Record<string, unknown>): DistractionReportStudent {
  return {
    student_id: String(raw.student_id ?? ""),
    name: String(raw.name ?? ""),
    present: Boolean(raw.present),
    active: Boolean(raw.active),
    attended_minutes: Number(raw.attended_minutes ?? 0),
    tab_switches: Number(raw.tab_switches ?? 0),
    tab_switch_seconds: Number(raw.tab_switch_seconds ?? 0),
    tab_switch_minutes: Number(raw.tab_switch_minutes ?? 0),
    minimize_count: Number(raw.minimize_count ?? 0),
    minimize_seconds: Number(raw.minimize_seconds ?? 0),
    minimize_minutes: Number(raw.minimize_minutes ?? 0),
    alert_popup_events: Number(raw.alert_popup_events ?? 0),
    alert_popup_seconds: Number(raw.alert_popup_seconds ?? 0),
    alert_popup_minutes: Number(raw.alert_popup_minutes ?? 0),
    missed_quizzes: Number(raw.missed_quizzes ?? 0),
    poll_non_responses: Number(raw.poll_non_responses ?? 0),
  };
}

export async function fetchDistractionReport(
  sessionId: string,
): Promise<DistractionReport> {
  const res = await authService.get(
    `/session/sessions/${sessionId}/distraction-report/`,
  );
  const data = res?.data || {};
  const summaryRaw = (data.summary || {}) as Record<string, unknown>;
  const studentsRaw = Array.isArray(data.students) ? data.students : [];
  return {
    success: Boolean(data.success),
    session_id: String(data.session_id ?? sessionId),
    summary: {
      students_tracked: Number(
        summaryRaw.students_tracked ?? emptySummary.students_tracked,
      ),
      tab_switches: Number(summaryRaw.tab_switches ?? 0),
      tab_switch_seconds: Number(summaryRaw.tab_switch_seconds ?? 0),
      tab_switch_minutes: Number(summaryRaw.tab_switch_minutes ?? 0),
      minimize_count: Number(summaryRaw.minimize_count ?? 0),
      minimize_seconds: Number(summaryRaw.minimize_seconds ?? 0),
      minimize_minutes: Number(summaryRaw.minimize_minutes ?? 0),
      alert_popup_events: Number(summaryRaw.alert_popup_events ?? 0),
      alert_popup_seconds: Number(summaryRaw.alert_popup_seconds ?? 0),
      alert_popup_minutes: Number(summaryRaw.alert_popup_minutes ?? 0),
      missed_quizzes: Number(summaryRaw.missed_quizzes ?? 0),
      poll_non_responses: Number(summaryRaw.poll_non_responses ?? 0),
      completed_quizzes: Number(summaryRaw.completed_quizzes ?? 0),
      ended_polls: Number(summaryRaw.ended_polls ?? 0),
    },
    students: studentsRaw.map((row: Record<string, unknown>) => asStudent(row)),
  };
}
